import { useState, useEffect, useRef } from 'react';
import './App.css';
import { translations } from './translations';

// Animated counter component
const AnimatedCounter = ({ end, duration = 2000, prefix = '', suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

// Timeline event component
const TimelineEvent = ({ time, event, status, delay }) => (
  <div 
    className="timeline-event" 
    style={{ animationDelay: `${delay}ms` }}
    data-status={status}
  >
    <span className="timeline-time">{time}</span>
    <span className="timeline-dot"></span>
    <span className="timeline-label">{event}</span>
    {status && <span className={`timeline-status ${status}`}>{status === 'success' ? '✓' : status === 'error' ? '✗' : '⚠'}</span>}
  </div>
);

// Feature card component
const FeatureCard = ({ icon, title, description, delay }) => (
  <div className="feature-card" style={{ animationDelay: `${delay}ms` }}>
    <div className="feature-icon">{icon}</div>
    <h3>{title}</h3>
    <p>{description}</p>
  </div>
);

// Problem card component
const ProblemCard = ({ emoji, title, description }) => (
  <div className="problem-card">
    <span className="problem-emoji">{emoji}</span>
    <h4>{title}</h4>
    <p>{description}</p>
  </div>
);

// Pricing card component
const PricingCard = ({ plan, t }) => (
  <div className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
    {plan.popular && <span className="popular-badge">{t.pricing.popular}</span>}
    <h3>{plan.name}</h3>
    <div className="price">
      <span className="amount">${plan.price}</span>
      {!plan.isFree && <span className="period">{t.pricing.perMonth}</span>}
    </div>
    <p className="payments">{plan.payments} {t.pricing.paymentsPerMonth}</p>
    <ul className="features-list">
      {plan.features.map((feature, i) => (
        <li key={i}><span className="check">✓</span> {feature}</li>
      ))}
    </ul>
    <a href="https://app.silentloss.io" className="pricing-cta">{t.pricing.startButton}</a>
  </div>
);

// Language Switcher Component
const LanguageSwitcher = ({ currentLang, onChangeLang }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleLanguageChange = (newLang) => {
    // Mark that user manually changed language
    localStorage.setItem('silentloss-lang-manual', 'true');
    onChangeLang(newLang);
    setIsOpen(false);
  };

  return (
    <div className="language-switcher">
      <button 
        className="lang-btn"
        onClick={() => setIsOpen(!isOpen)}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
        <span>{currentLang.toUpperCase()}</span>
        <svg className={`chevron ${isOpen ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {isOpen && (
        <div className="lang-dropdown">
          <button 
            className={currentLang === 'tr' ? 'active' : ''}
            onClick={() => handleLanguageChange('tr')}
          >
            🇹🇷 Türkçe
          </button>
          <button 
            className={currentLang === 'en' ? 'active' : ''}
            onClick={() => handleLanguageChange('en')}
          >
            🇬🇧 English
          </button>
        </div>
      )}
    </div>
  );
};

function App() {
  const [activeSection, setActiveSection] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [lang, setLang] = useState(() => {
    // 1. Check URL parameter first (for SEO sharing)
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    if (urlLang === 'en' || urlLang === 'tr') {
      return urlLang;
    }
    
    // 2. Check if user manually changed language before (localStorage)
    const userManuallyChanged = localStorage.getItem('silentloss-lang-manual');
    if (userManuallyChanged === 'true') {
      const saved = localStorage.getItem('silentloss-lang');
      if (saved === 'en' || saved === 'tr') {
        return saved;
      }
    }
    
    // 3. Auto-detect from browser language - TR ise TR, değilse EN
    const browserLang = navigator.language || navigator.userLanguage || 'en';
    const langCode = browserLang.toLowerCase().split('-')[0];
    return langCode === 'tr' ? 'tr' : 'en';
  });

  const t = translations[lang];

  // Update HTML lang attribute, localStorage, and meta tags when language changes
  useEffect(() => {
    // Update localStorage
    localStorage.setItem('silentloss-lang', lang);
    
    // Update HTML lang attribute
    document.documentElement.lang = lang;
    
    // Update URL without reload (for SEO and sharing)
    const url = new URL(window.location);
    url.searchParams.set('lang', lang);
    window.history.replaceState({}, '', url);
    
    // Update meta tags dynamically
    const metaDescription = document.querySelector('meta[name="description"]');
    const metaTitle = document.querySelector('meta[name="title"]');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    
    const titles = {
      en: 'SilentLoss — Payment & Revenue Safety Platform',
      tr: 'SilentLoss — Ödeme & Gelir Güvenliği Platformu'
    };
    
    const descriptions = {
      en: 'Detect silent revenue loss, duplicate payments, and broken flows in your payment infrastructure. Monitor every transaction end-to-end with provable evidence.',
      tr: 'Ödeme altyapınızdaki sessiz gelir kayıplarını, çift ödemeleri ve bozuk akışları tespit edin. Her işlemi uçtan uca kanıtlanabilir şekilde izleyin.'
    };
    
    document.title = titles[lang];
    if (metaTitle) metaTitle.content = titles[lang];
    if (metaDescription) metaDescription.content = descriptions[lang];
    if (ogTitle) ogTitle.content = titles[lang];
    if (ogDescription) ogDescription.content = descriptions[lang];
    if (twitterTitle) twitterTitle.content = titles[lang];
    if (twitterDescription) twitterDescription.content = descriptions[lang];
    
    // Update og:locale
    const ogLocale = document.querySelector('meta[property="og:locale"]');
    if (ogLocale) ogLocale.content = lang === 'tr' ? 'tr_TR' : 'en_US';
    
  }, [lang]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const sections = ['problem', 'features', 'how-it-works', 'pricing'];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const featureIcons = [
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 3v18h18"/>
      <path d="M7 14l4-4 4 4 6-6"/>
    </svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      <path d="M9 12l2 2 4-4"/>
    </svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M4 4h16v16H4z"/>
      <path d="M9 9h6v6H9z"/>
      <path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3"/>
    </svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 6v6l4 2"/>
    </svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="17 8 12 3 7 8"/>
      <line x1="12" y1="3" x2="12" y2="15"/>
    </svg>,
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 2a10 10 0 1 0 10 10"/>
      <path d="M12 6a6 6 0 0 1 6 6"/>
      <circle cx="12" cy="12" r="2"/>
    </svg>
  ];

  return (
    <div className="App">
      {/* Animated background */}
      <div className="bg-grid"></div>
      <div className="bg-glow bg-glow-1"></div>
      <div className="bg-glow bg-glow-2"></div>

      {/* Navigation */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <a href="#" className="logo">
            <svg viewBox="0 0 32 32" className="logo-icon">
              <path d="M16 2L4 8v8c0 7.7 5.1 14.9 12 17 6.9-2.1 12-9.3 12-17V8L16 2z" fill="none" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 16l3 3 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>SilentLoss</span>
          </a>

          <div className={`nav-links ${mobileMenuOpen ? 'open' : ''}`}>
            <a href="#problem" className={activeSection === 'problem' ? 'active' : ''}>{t.nav.problem}</a>
            <a href="#features" className={activeSection === 'features' ? 'active' : ''}>{t.nav.features}</a>
            <a href="#how-it-works" className={activeSection === 'how-it-works' ? 'active' : ''}>{t.nav.howItWorks}</a>
            <a href="#pricing" className={activeSection === 'pricing' ? 'active' : ''}>{t.nav.pricing}</a>
            <a href="http://developer.silentloss.io/" target="_blank" rel="noopener noreferrer">{t.nav.developer}</a>
          </div>

          <div className="nav-actions">
            <LanguageSwitcher currentLang={lang} onChangeLang={setLang} />
            <a href="https://app.silentloss.io" className="btn-ghost">{t.nav.login}</a>
            <a href="https://app.silentloss.io" className="btn-primary">{t.nav.requestDemo}</a>
          </div>

          <button 
            className={`mobile-menu-btn ${mobileMenuOpen ? 'open' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge">
            <span className="pulse"></span>
            <span>{t.hero.badge}</span>
          </div>

          <h1>
            {t.hero.titlePart1}
            <span className="gradient-text">{t.hero.titleHighlight}</span>
            {t.hero.titlePart2}
          </h1>

          <p className="hero-description">
            {t.hero.description} <strong>{t.hero.descriptionBold}</strong> {t.hero.descriptionEnd}
          </p>

          <div className="hero-stats">
            <div className="stat">
              <span className="stat-value"><AnimatedCounter end={5} suffix={t.hero.stat1Value} /></span>
              <span className="stat-label">{t.hero.stat1Label}</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat">
              <span className="stat-value"><AnimatedCounter end={2} suffix={t.hero.stat2Value} /></span>
              <span className="stat-label">{t.hero.stat2Label}</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat">
              <span className="stat-value"><AnimatedCounter end={99} suffix={t.hero.stat3Value} /></span>
              <span className="stat-label">{t.hero.stat3Label}</span>
            </div>
          </div>

          <div className="hero-cta">
            <a href="https://app.silentloss.io" className="btn-primary btn-large">
              <span>{t.hero.ctaPrimary}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </a>
            <a href="https://app.silentloss.io" className="btn-ghost btn-large">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polygon points="10 8 16 12 10 16 10 8" fill="currentColor"/>
              </svg>
              <span>{t.hero.ctaSecondary}</span>
            </a>
          </div>

          <div className="hero-trust">
            <span>{t.hero.trustedBy}</span>
            <div className="trust-logos">
              <div className="trust-logo">Stripe</div>
              <div className="trust-logo">Iyzico</div>
              <div className="trust-logo">PayTR</div>
              <div className="trust-logo">Shopify</div>
            </div>
          </div>
        </div>

        <div className="hero-visual">
          <div className="terminal">
            <div className="terminal-header">
              <div className="terminal-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span className="terminal-title">payment_timeline.log</span>
            </div>
            <div className="terminal-body">
              <div className="code-line">
                <span className="code-time">12:01:22</span>
                <span className="code-tag tag-init">INIT</span>
                <span className="code-text">payment_intent_id: pi_3Mx...</span>
              </div>
              <div className="code-line">
                <span className="code-time">12:01:23</span>
                <span className="code-tag tag-provider">PROVIDER</span>
                <span className="code-text">iyzico.request_sent</span>
              </div>
              <div className="code-line">
                <span className="code-time">12:01:25</span>
                <span className="code-tag tag-success">SUCCESS</span>
                <span className="code-text">provider_confirmed: ₺199.90</span>
              </div>
              <div className="code-line">
                <span className="code-time">12:01:26</span>
                <span className="code-tag tag-webhook">WEBHOOK</span>
                <span className="code-text">received (attempt: 2)</span>
              </div>
              <div className="code-line highlight-error">
                <span className="code-time">12:01:27</span>
                <span className="code-tag tag-error">ERROR</span>
                <span className="code-text">db_write_failed: timeout</span>
              </div>
              <div className="code-line">
                <span className="code-time">12:03:00</span>
                <span className="code-tag tag-alert">ALERT</span>
                <span className="code-text">revenue_missing: ₺199.90 {lang === 'tr' ? 'kayıp' : 'lost'}</span>
              </div>
            </div>
          </div>

          <div className="floating-card card-1">
            <div className="card-icon error">!</div>
            <div className="card-content">
              <span className="card-title">{lang === 'tr' ? 'Kayıp Gelir Tespit Edildi' : 'Lost Revenue Detected'}</span>
              <span className="card-value">₺12,450</span>
            </div>
          </div>

          <div className="floating-card card-2">
            <div className="card-icon warning">⚠</div>
            <div className="card-content">
              <span className="card-title">{lang === 'tr' ? 'Çift Webhook' : 'Double Webhook'}</span>
              <span className="card-value">3 {lang === 'tr' ? 'işlem' : 'transactions'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section id="problem" className="section section-problem">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">{t.problem.tag}</span>
            <h2>{t.problem.title} <span className="gradient-text">{t.problem.titleHighlight}</span></h2>
            <p>{t.problem.subtitle}</p>
          </div>

          <div className="problems-grid">
            {t.problem.cards.map((card, i) => (
              <ProblemCard 
                key={i}
                emoji={card.emoji}
                title={card.title}
                description={card.description}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section section-features">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">{t.features.tag}</span>
            <h2>{t.features.title} <span className="gradient-text">{t.features.titleHighlight}</span></h2>
            <p>{t.features.subtitle}</p>
          </div>

          <div className="features-grid">
            {t.features.cards.map((card, i) => (
              <FeatureCard 
                key={i}
                icon={featureIcons[i]}
                title={card.title}
                description={card.description}
                delay={i * 100}
              />
            ))}
          </div>

          <div className="features-highlight">
            <div className="highlight-visual">
              <div className="state-flow">
                <div className="state-node active">INIT</div>
                <div className="state-arrow">→</div>
                <div className="state-node active">PROVIDER</div>
                <div className="state-arrow">→</div>
                <div className="state-node active">SUCCESS</div>
                <div className="state-arrow">→</div>
                <div className="state-node">WEBHOOK</div>
                <div className="state-arrow error">✗</div>
                <div className="state-node error">DB_WRITE</div>
              </div>
            </div>
            <div className="highlight-text">
              <h3>{t.features.highlight.title}</h3>
              <p>{t.features.highlight.description}</p>
              <ul>
                {t.features.highlight.features.map((feature, i) => (
                  <li key={i}><span className="check">✓</span> {feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="section section-how">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">{t.howItWorks.tag}</span>
            <h2>{t.howItWorks.title} <span className="gradient-text">{t.howItWorks.titleHighlight}</span></h2>
            <p>{t.howItWorks.subtitle}</p>
          </div>

          <div className="steps-container">
            <div className="step">
              <div className="step-number">01</div>
              <div className="step-content">
                <h3>{t.howItWorks.steps[0].title}</h3>
                <p>{t.howItWorks.steps[0].description}</p>
                <div className="step-code">
                  <pre>{`
SilentLoss::paymentInitialized([
    'payment_intent_id' => $orderId,
    'amount' => 250.00,
    'currency' => 'USD',
    'user_id' => $userId,
    'metadata' => [
        'customer_email' => 'john@example.com',
        'device_type' => 'desktop',
    ],
]);`}</pre>
                </div>
              </div>
            </div>

            <div className="step step-webhook-animation">
              <div className="step-number">02</div>
              <div className="step-content">
                <h3>{t.howItWorks.steps[1].title}</h3>
                <p className="step-tagline">{t.howItWorks.steps[1].description}</p>
                
                {/* Payment Flow Animation */}
                <div className="payment-flow-container">
                  {/* Scenario Tabs */}
                  <div className="scenario-tabs">
                    <div className="scenario-tab healthy">
                      <span className="tab-icon">✓</span>
                      <span>{t.howItWorks.animation.scenarioHealthy}</span>
                    </div>
                    <div className="scenario-tab loss">
                      <span className="tab-icon">⚠</span>
                      <span>{t.howItWorks.animation.scenarioLoss}</span>
                    </div>
                  </div>

                  {/* Flow Animation */}
                  <div className="flow-animation">
                    {/* Step 1: Client */}
                    <div className="flow-node node-client">
                      <div className="node-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="5" y="2" width="14" height="20" rx="2"/>
                          <path d="M12 18h.01"/>
                        </svg>
                      </div>
                      <div className="node-content">
                        <span className="node-label">{t.howItWorks.animation.labels.client}</span>
                        <span className="node-event">{t.howItWorks.animation.events.paymentInitiated}</span>
                      </div>
                      <div className="node-status status-success">
                        <span className="status-icon">✓</span>
                        <span>{t.howItWorks.animation.status.initiating}</span>
                      </div>
                      <div className="node-pulse"></div>
                    </div>

                    {/* Connector 1 */}
                    <div className="flow-connector connector-1">
                      <div className="connector-line"></div>
                      <div className="connector-packet packet-down-1"></div>
                      <div className="connector-packet packet-down-2"></div>
                    </div>

                    {/* Step 2: Payment Provider */}
                    <div className="flow-node node-provider">
                      <div className="node-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="1" y="4" width="22" height="16" rx="2"/>
                          <path d="M1 10h22"/>
                        </svg>
                      </div>
                      <div className="node-content">
                        <span className="node-label">{t.howItWorks.animation.labels.provider}</span>
                        <span className="node-event">{t.howItWorks.animation.events.processingPayment}</span>
                      </div>
                      <div className="node-status status-processing">
                        <span className="status-spinner"></span>
                        <span>{t.howItWorks.animation.status.processing}</span>
                      </div>
                      <div className="node-status status-success delayed">
                        <span className="status-icon">✓</span>
                        <span>{t.howItWorks.animation.events.providerSuccess}</span>
                      </div>
                      <div className="node-pulse delayed"></div>
                    </div>

                    {/* Connector 2 - Split */}
                    <div className="flow-connector connector-split">
                      <div className="split-point">
                        <div className="split-dot"></div>
                      </div>
                      <div className="split-branches">
                        <div className="branch-packet packet-merchant-1"></div>
                        <div className="branch-packet packet-merchant-2"></div>
                        <div className="branch-packet packet-sl-1"></div>
                        <div className="branch-packet packet-sl-2"></div>
                      </div>
                    </div>

                    {/* Step 3: Two Targets */}
                    <div className="flow-targets">
                      {/* Merchant Server */}
                      <div className="flow-node node-merchant">
                        <div className="node-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="2" y="3" width="20" height="14" rx="2"/>
                            <path d="M8 21h8M12 17v4"/>
                          </svg>
                        </div>
                        <div className="node-content">
                          <span className="node-label">{t.howItWorks.animation.labels.merchantServer}</span>
                          <span className="node-event">{t.howItWorks.animation.events.merchantRecorded}</span>
                        </div>
                        <div className="node-status status-success delayed-2">
                          <span className="status-icon">✓</span>
                          <span>{t.howItWorks.animation.status.recorded}</span>
                        </div>
                        <div className="node-pulse delayed-2"></div>
                      </div>

                      {/* SilentLoss */}
                      <div className="flow-node node-silentloss">
                        <div className="node-icon silentloss-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                            <path d="M9 12l2 2 4-4"/>
                          </svg>
                        </div>
                        <div className="node-content">
                          <span className="node-label">SilentLoss</span>
                          <span className="node-event">{t.howItWorks.animation.events.silentlossConfirmed}</span>
                        </div>
                        <div className="node-status status-success delayed-2">
                          <span className="status-icon">✓</span>
                          <span>{t.howItWorks.animation.status.confirmed}</span>
                        </div>
                        <div className="node-pulse delayed-2 silentloss-pulse"></div>
                      </div>
                    </div>

                    {/* Connector 3 - Merchant to SilentLoss */}
                    <div className="flow-connector connector-verification">
                      <div className="verification-line healthy-line">
                        <div className="verification-packet v-packet-1"></div>
                        <div className="verification-packet v-packet-2"></div>
                      </div>
                      <div className="verification-line loss-line">
                        <div className="missing-indicator">
                          <span className="missing-icon">✗</span>
                          <span className="missing-text">{t.howItWorks.animation.status.missing}</span>
                        </div>
                      </div>
                    </div>

                    {/* Final Result */}
                    <div className="flow-result">
                      {/* Healthy Result */}
                      <div className="result-card result-healthy">
                        <div className="result-icon success">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M9 12l2 2 4-4"/>
                          </svg>
                        </div>
                        <div className="result-content">
                          <h4>{t.howItWorks.animation.healthyMessage.title}</h4>
                          <p>{t.howItWorks.animation.healthyMessage.subtitle}</p>
                        </div>
                        <div className="result-glow success"></div>
                      </div>

                      {/* Loss Result */}
                      <div className="result-card result-loss">
                        <div className="result-icon danger">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                            <path d="M12 9v4M12 17h.01"/>
                          </svg>
                        </div>
                        <div className="result-content">
                          <h4>{t.howItWorks.animation.lossMessage.title}</h4>
                          <p>{t.howItWorks.animation.lossMessage.subtitle}</p>
                          <span className="result-detail">{t.howItWorks.animation.lossMessage.detail}</span>
                        </div>
                        <div className="result-glow danger"></div>
                      </div>
                    </div>
                  </div>

                  {/* Animation CTA */}
                  <div className="flow-cta">
                    <h3>{t.howItWorks.animation.cta.title}</h3>
                    <p>{t.howItWorks.animation.cta.subtitle}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="step">
              <div className="step-number">03</div>
              <div className="step-content">
                <h3>{t.howItWorks.steps[2].title}</h3>
                <p>{t.howItWorks.steps[2].description}</p>
                <div className="step-metrics">
                  <div className="metric">
                    <span className="metric-value">2,847</span>
                    <span className="metric-label">{t.howItWorks.metrics.total}</span>
                  </div>
                  <div className="metric success">
                    <span className="metric-value">2,801</span>
                    <span className="metric-label">{t.howItWorks.metrics.healthy}</span>
                  </div>
                  <div className="metric warning">
                    <span className="metric-value">32</span>
                    <span className="metric-label">{t.howItWorks.metrics.risky}</span>
                  </div>
                  <div className="metric error">
                    <span className="metric-value">14</span>
                    <span className="metric-label">{t.howItWorks.metrics.lost}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="architecture-note">
            <div className="note-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4M12 8h.01"/>
              </svg>
            </div>
            <div className="note-content">
              <h4>{t.howItWorks.note.title}</h4>
              <p>{t.howItWorks.note.description} <strong>{t.howItWorks.note.descriptionBold}</strong> {t.howItWorks.note.descriptionEnd}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard Preview Section */}
      <section className="section section-dashboard">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">{t.dashboard.tag}</span>
            <h2>{t.dashboard.title} <span className="gradient-text">{t.dashboard.titleHighlight}</span></h2>
          </div>

          <div className="dashboard-preview">
            <div className="dashboard-window">
              <div className="window-header">
                <div className="window-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className="window-title">{t.dashboard.windowTitle}</span>
              </div>
              <div className="window-content">
                <div className="dashboard-sidebar">
                  <div className="sidebar-item active">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7"/>
                      <rect x="14" y="3" width="7" height="7"/>
                      <rect x="14" y="14" width="7" height="7"/>
                      <rect x="3" y="14" width="7" height="7"/>
                    </svg>
                    {t.dashboard.sidebar.overview}
                  </div>
                  <div className="sidebar-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                    </svg>
                    {t.dashboard.sidebar.payments}
                  </div>
                  <div className="sidebar-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    </svg>
                    {t.dashboard.sidebar.risks}
                  </div>
                  <div className="sidebar-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                    {t.dashboard.sidebar.reports}
                  </div>
                </div>
                <div className="dashboard-main">
                  <div className="dashboard-stats">
                    <div className="stat-card">
                      <span className="stat-label">{t.dashboard.stats.totalPayment}</span>
                      <span className="stat-value">₺847,320</span>
                      <span className="stat-change positive">+12.5%</span>
                    </div>
                    <div className="stat-card">
                      <span className="stat-label">{t.dashboard.stats.healthy}</span>
                      <span className="stat-value">98.2%</span>
                      <span className="stat-indicator success"></span>
                    </div>
                    <div className="stat-card alert">
                      <span className="stat-label">{t.dashboard.stats.estimatedLoss}</span>
                      <span className="stat-value">₺12,450</span>
                      <span className="stat-change negative">{t.dashboard.stats.thisWeek}</span>
                    </div>
                  </div>
                  <div className="dashboard-chart">
                    <div className="chart-header">
                      <span>{t.dashboard.chart.title}</span>
                    </div>
                    <div className="chart-bars">
                      {t.dashboard.chart.days.map((day, i) => (
                        <div className="bar-group" key={i}>
                          <div className="bar success" style={{height: `${85 + Math.random() * 10}%`}}></div>
                          <div className="bar warning" style={{height: `${5 + Math.random() * 10}%`}}></div>
                          <div className="bar error" style={{height: `${2 + Math.random() * 8}%`}}></div>
                          <span className="bar-label">{day}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="dashboard-timeline">
                    <div className="timeline-header">
                      <span>{t.dashboard.timeline.title}</span>
                    </div>
                    <div className="timeline-items">
                      <TimelineEvent time="14:32" event={`${t.dashboard.timeline.lostRevenue}: pi_3Mx8K...`} status="error" delay={0} />
                      <TimelineEvent time="13:15" event={`${t.dashboard.timeline.doubleWebhook}: pi_2Nq...`} status="warning" delay={100} />
                      <TimelineEvent time="11:47" event={`${t.dashboard.timeline.stateMismatch}: pi_1Kp...`} status="warning" delay={200} />
                      <TimelineEvent time="09:22" event={`${t.dashboard.timeline.timeout}: pi_9Rt2L...`} status="error" delay={300} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Section */}
      <section className="section section-ai">
        <div className="container">
          <div className="ai-content">
            <div className="ai-text">
              <span className="section-tag">{t.ai.tag}</span>
              <h2>{t.ai.title} <span className="gradient-text">{t.ai.titleHighlight}</span></h2>
              <p>{t.ai.description}</p>
              
              <div className="ai-example">
                <div className="ai-avatar">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 2a10 10 0 1 0 10 10"/>
                    <path d="M12 6a6 6 0 0 1 6 6"/>
                    <circle cx="12" cy="12" r="2"/>
                  </svg>
                </div>
                <div className="ai-message">
                  <p className="ai-quote">{t.ai.quote} <strong>{t.ai.quoteBold1}</strong> {t.ai.quoteMiddle} <strong>{t.ai.quoteBold2}</strong> {t.ai.quoteEnd}</p>
                  <span className="ai-meta">{t.ai.meta}</span>
                </div>
              </div>

              <ul className="ai-capabilities">
                {t.ai.capabilities.map((cap, i) => (
                  <li key={i}><span className="check">✓</span> {cap}</li>
                ))}
              </ul>
            </div>

            <div className="ai-visual">
              <div className="ai-card">
                <div className="ai-card-header">
                  <span>{t.ai.card.title}</span>
                  <span className="ai-badge">{t.ai.card.new}</span>
                </div>
                <div className="ai-insights">
                  <div className="insight">
                    <div className="insight-icon trend-down">↓</div>
                    <div className="insight-content">
                      <span className="insight-title">{t.ai.card.insights[0].title}</span>
                      <span className="insight-value">{t.ai.card.insights[0].value}</span>
                    </div>
                  </div>
                  <div className="insight">
                    <div className="insight-icon pattern">◎</div>
                    <div className="insight-content">
                      <span className="insight-title">{t.ai.card.insights[1].title}</span>
                      <span className="insight-value">{t.ai.card.insights[1].value}</span>
                    </div>
                  </div>
                  <div className="insight">
                    <div className="insight-icon action">→</div>
                    <div className="insight-content">
                      <span className="insight-title">{t.ai.card.insights[2].title}</span>
                      <span className="insight-value">{t.ai.card.insights[2].value}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="section section-pricing">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">{t.pricing.tag}</span>
            <h2>{t.pricing.title} <span className="gradient-text">{t.pricing.titleHighlight}</span></h2>
            <p>{t.pricing.subtitle}</p>
          </div>

          <div className="pricing-grid">
            {t.pricing.plans.map((plan, i) => (
              <PricingCard 
                key={i}
                plan={plan}
                t={t}
              />
            ))}
          </div>

          <div className="pricing-audit">
            <div className="audit-content">
              <div className="audit-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
                  <path d="M9 5a2 2 0 012-2h2a2 2 0 012 2v0a2 2 0 01-2 2h-2a2 2 0 01-2-2v0z"/>
                  <path d="M9 12h6M9 16h6"/>
                </svg>
              </div>
              <div className="audit-text">
                <h3>{t.pricing.audit.title}</h3>
                <p>{t.pricing.audit.description}</p>
                <span className="audit-price">{t.pricing.audit.price}</span>
              </div>
              <button className="btn-primary">{t.pricing.audit.button}</button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-cta">
        <div className="container">
          <div className="cta-content">
            <h2>{t.cta.title} <span className="gradient-text">{t.cta.titleHighlight}</span> {t.cta.titleEnd}</h2>
            <p>{t.cta.description}</p>
            <div className="cta-actions">
              <a href="https://app.silentloss.io" className="btn-primary btn-large">
                <span>{t.cta.primaryButton}</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </a>
              <a href="https://app.silentloss.io" className="btn-ghost btn-large">{t.cta.secondaryButton}</a>
            </div>
            <div className="cta-trust">
              <div className="trust-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                <span>{t.cta.trust.gdpr}</span>
              </div>
              <div className="trust-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <span>{t.cta.trust.pci}</span>
              </div>
              <div className="trust-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 6v6l4 2"/>
                </svg>
                <span>{t.cta.trust.uptime}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <a href="#" className="logo">
                <svg viewBox="0 0 32 32" className="logo-icon">
                  <path d="M16 2L4 8v8c0 7.7 5.1 14.9 12 17 6.9-2.1 12-9.3 12-17V8L16 2z" fill="none" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 16l3 3 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>SilentLoss</span>
              </a>
              <p>{t.footer.description}</p>
            </div>

            <div className="footer-links">
              <h4>{t.footer.product.title}</h4>
              <a href="#features">{t.footer.product.features}</a>
              <a href="#pricing">{t.footer.product.pricing}</a>
              <a href="#how-it-works">{t.footer.product.howItWorks}</a>
              <a href="#">{t.footer.product.documentation}</a>
            </div>

            <div className="footer-links">
              <h4>{t.footer.company.title}</h4>
              <a href="#">{t.footer.company.about}</a>
              <a href="#">{t.footer.company.blog}</a>
              <a href="#">{t.footer.company.careers}</a>
              <a href="#">{t.footer.company.contact}</a>
            </div>

            <div className="footer-links">
              <h4>{t.footer.support.title}</h4>
              <a href="#">{t.footer.support.helpCenter}</a>
              <a href="#">{t.footer.support.apiReference}</a>
              <a href="#">{t.footer.support.status}</a>
              <a href="#">{t.footer.support.security}</a>
            </div>
          </div>

          <div className="footer-bottom">
            <p>{t.footer.legal.copyright}</p>
            <div className="footer-legal">
              <a href="#">{t.footer.legal.privacy}</a>
              <a href="#">{t.footer.legal.terms}</a>
              <a href="#">{t.footer.legal.kvkk}</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
